import { useCallback, useEffect, useState } from "react";
import type { Selection } from "react-aria-components";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { BarChartSquare02, Building02, CurrencyDollarCircle, Download01, FileCheck02, Inbox01, Mail01, MessageQuestionCircle, SearchLg, Users01 } from "@untitledui/icons";
import { CommandMenu, type CommandMenuGroupType } from "@/components/application/command-menus/command-menu";
import type { CommandDropdownMenuItemProps } from "@/components/application/command-menus/base-components/command-menu-item";
import { useApplications, useCustomers, useDownloadNotify, useLeads, useMembers, useNewsletter } from "@/hooks/use-admin-data";
import { realOrgs } from "@/lib/org-filter";

export function CommandSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  // Admin data sources: customers/orgs from the Shield DB; newsletter,
  // applications and leads from the qadar-website DB; console members.
  const { data: customers = [] } = useCustomers();
  const { data: newsletter = [] } = useNewsletter();
  const { data: applications = [] } = useApplications();
  const { data: leads = [] } = useLeads();
  const { data: downloadNotify = [] } = useDownloadNotify();
  const { data: members = [] } = useMembers();

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setIsOpen(true);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  const groups: CommandMenuGroupType[] = [
    {
      id: "navigation",
      title: t("shell.commandMenu.navigation"),
      items: [
        { id: "nav-dashboard", type: "icon", icon: BarChartSquare02, label: t("shell.nav.dashboard"), shortcutKeys: [] },
        { id: "nav-customers", type: "icon", icon: Building02, label: t("shell.nav.customers"), shortcutKeys: [] },
        { id: "nav-revenue", type: "icon", icon: CurrencyDollarCircle, label: t("shell.nav.revenue"), shortcutKeys: [] },
        { id: "nav-newsletter", type: "icon", icon: Mail01, label: t("shell.nav.newsletter"), shortcutKeys: [] },
        { id: "nav-applications", type: "icon", icon: FileCheck02, label: t("shell.nav.applications"), shortcutKeys: [] },
        { id: "nav-leads", type: "icon", icon: Inbox01, label: t("shell.nav.leads"), shortcutKeys: [] },
        { id: "nav-download-notify", type: "icon", icon: Download01, label: t("shell.nav.downloadNotify"), shortcutKeys: [] },
        { id: "nav-help-feedback", type: "icon", icon: MessageQuestionCircle, label: t("shell.nav.helpFeedback"), shortcutKeys: [] },
        { id: "nav-seo", type: "icon", icon: SearchLg, label: t("shell.nav.seo"), shortcutKeys: [] },
        { id: "nav-members", type: "icon", icon: Users01, label: t("shell.nav.members"), shortcutKeys: [] },
      ] as CommandDropdownMenuItemProps[],
    },
    {
      id: "customers",
      title: t("shell.nav.customers"),
      items: realOrgs(customers)
        .slice(0, 20)
        .map((c) => ({
          id: `customer-${c.id}`,
          type: "icon" as const,
          icon: Building02,
          label: c.name,
          description: [c.domain, c.plan].filter(Boolean).join(" · "),
        })),
    },
    {
      id: "newsletter",
      title: t("shell.nav.newsletter"),
      items: newsletter.slice(0, 20).map((r) => ({ id: `news-${r.id}`, type: "default" as const, label: r.email, description: r.status })),
    },
    {
      id: "applications",
      title: t("shell.nav.applications"),
      items: applications.slice(0, 20).map((r) => ({ id: `app-${r.id}`, type: "default" as const, label: r.name ?? r.email, description: r.role })),
    },
    {
      id: "leads",
      title: t("shell.nav.leads"),
      items: leads.slice(0, 20).map((r) => ({ id: `lead-${r.id}`, type: "default" as const, label: r.company, description: r.name ?? r.email })),
    },
    {
      id: "download-notify",
      title: t("shell.nav.downloadNotify"),
      items: downloadNotify.slice(0, 20).map((r) => ({ id: `dln-${r.id}`, type: "default" as const, label: r.email, description: r.platforms.join(", ") })),
    },
    {
      id: "members",
      title: t("shell.nav.members"),
      items: members.slice(0, 20).map((r) => ({ id: `member-${r.id}`, type: "default" as const, label: r.email ?? r.id })),
    },
  ].filter((g) => g.items.length > 0);

  const handleSelection = useCallback(
    (keys: Selection) => {
      if (keys === "all") return;
      const key = [...keys][0];
      if (!key) return;
      const id = String(key);

      const navMap: Record<string, string> = {
        "nav-dashboard": "/",
        "nav-customers": "/customers",
        "nav-revenue": "/revenue",
        "nav-newsletter": "/newsletter",
        "nav-applications": "/applications",
        "nav-leads": "/leads",
        "nav-download-notify": "/download-notify",
        "nav-help-feedback": "/help-feedback",
        "nav-seo": "/seo",
        "nav-members": "/members",
      };

      if (navMap[id]) navigate(navMap[id]);
      else if (id.startsWith("customer-")) navigate("/customers");
      else if (id.startsWith("news-")) navigate("/newsletter");
      else if (id.startsWith("app-")) navigate("/applications");
      else if (id.startsWith("lead-")) navigate("/leads");
      else if (id.startsWith("dln-")) navigate("/download-notify");
      else if (id.startsWith("member-")) navigate("/members");
      setIsOpen(false);
    },
    [navigate],
  );

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 rounded-lg border border-secondary bg-primary px-3 py-1.5 text-sm text-placeholder transition duration-100 ease-linear hover:border-primary hover:bg-primary_hover"
      >
        <SearchLg className="size-4 text-fg-quaternary" />
        <span className="max-lg:hidden">{t("shell.searchPlaceholder")}</span>
        <kbd className="ml-2 hidden rounded border border-secondary bg-secondary px-1.5 py-0.5 font-mono text-xs text-quaternary lg:inline-block">⌘K</kbd>
      </button>

      <CommandMenu isOpen={isOpen} onOpenChange={setIsOpen} items={groups} placeholder={t("shell.searchPlaceholder")} shortcut={null} onSelectionChange={handleSelection}>
        <CommandMenu.Group>
          <CommandMenu.List>
            {(section) => (
              <CommandMenu.Section title={section.title} items={section.items}>
                {(item) => <CommandMenu.Item {...item} />}
              </CommandMenu.Section>
            )}
          </CommandMenu.List>
        </CommandMenu.Group>
      </CommandMenu>
    </>
  );
}
